import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Lightweight Tailwind-based Modal replacement for react-bootstrap Modal.
 * Geschlossen wird bewusst nur über den Schließen-Button bzw. das X –
 * ein Klick auf den Hintergrund schließt das Modal nicht.
 * Props: show, onHide, children, size ('sm' | 'md' | 'lg' | 'xl'),
 * variant ('default' | 'danger' – rot dargestellt, z. B. für Warnungen)
 */
function Modal({ show, onHide, children, size = 'md', variant = 'default' }) {
  useEffect(() => {
    if (!show) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onHide?.();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [show, onHide]);

  if (!show) return null;

  const sizeClass = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  }[size] ?? 'max-w-lg';

  return createPortal(
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div className={`modal-panel w-full ${sizeClass}${variant === 'danger' ? ' modal-panel-danger' : ''}`}>
        {children}
      </div>
    </div>,
    document.body
  );
}

Modal.Header = function ModalHeader({ children, onClose }) {
  return (
    <div className="modal-header">
      <h2 className="modal-title">{children}</h2>
      {onClose && (
        <button
          onClick={onClose}
          className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-1"
          aria-label="Schließen"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
};

Modal.Body = function ModalBody({ children }) {
  return <div className="modal-body">{children}</div>;
};

Modal.Footer = function ModalFooter({ children }) {
  return <div className="modal-footer">{children}</div>;
};

export default Modal;
